import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
const ThemeContext = createContext();

//Theme Enum definition
export const ThemeMode = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',

  values: () => ['light', 'dark'],
  isValid: value => ['light', 'dark'].includes(value),
  getLabel: value =>
    ({
      light: 'Light',
      dark: 'Dark',
    })[value],
  getIcon: value =>
    ({
      light: <Sun />,
      dark: <Moon />,
    })[value],
});

const getSystemTheme = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? ThemeMode.DARK : ThemeMode.LIGHT;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('themeMode');
      if (saved) return ThemeMode.isValid(saved) ? saved : ThemeMode.DARK;
      // Get OS preference
      return getSystemTheme();
    }
    return ThemeMode.DARK;
  });

  // Listen for OS theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = e => {
      // Only update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? ThemeMode.DARK : ThemeMode.LIGHT);
      }
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // useLayoutEffect ensures theme class is applied before browser paints,
  // preventing a flash of unstyled content on first render
  useLayoutEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('dark', theme === ThemeMode.DARK);
    root.style.colorScheme = theme;
    localStorage.setItem('themeMode', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === ThemeMode.DARK ? ThemeMode.LIGHT : ThemeMode.DARK);
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme, ThemeMode }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
